/* global google */
import React from 'react';
import styled from 'styled-components/macro';
import { connect } from 'react-redux';
import { change } from 'redux-form';
import PropTypes from 'prop-types';

import { addressValidationError } from '../../containers/Widget/actions';
import { cacheFormData } from '../../utils/formCaching/actions';
import pollForValidAddressSelected from '../../utils/promisePoll';
import { copyFor, capitalization } from '../../copy/copy';
import { parseAddressComponents, formatAddressToString } from './helpers';

const Container = styled.div`
    position: relative;
    padding: ${props => 12 + props.paddingAdjustment}px 0;

    input {
        -webkit-appearance: none !important;
    }
`;
const Label = styled.label`
    font-family: Averta;
    pointer-events: none;
    line-height: 1.5;
    letter-spacing: 1px;
    padding: 0 5px;
    background-color: ${props =>
        props.active || (!props.active && props.value) ? `white` : `transparent`};
    font-weight: ${props => (props.active || props.value ? 600 : 400)};
    color: ${props => (props.error && !props.active ? `#ff245e;` : `#979191;`)};
    position: absolute;
    transition: all 250ms ease;
    left: 5px;
    text-transform: ${props =>
        props.active || props.value ? `uppercase` : `capitalize`};
    font-size: ${props => (props.active || props.value ? 8 : 14)}px;
    top: ${props =>
        (props.active || props.value !== '' ? 5 : 20) + props.paddingAdjustment + 'px'};
`;

class ReactGoogleAutocomplete extends React.Component {
    static propTypes = {
        addressValidationError: PropTypes.any,
        bounds: PropTypes.object,
        cachedAddress: PropTypes.string,
        cacheFormData: PropTypes.func,
        componentRestrictions: PropTypes.object,
        fields: PropTypes.arrayOf(PropTypes.string),
        formName: PropTypes.arrayOf(PropTypes.string),
        previouslyCachedAddress: PropTypes.object,
        change: PropTypes.func,
        onFocus: PropTypes.func,
        onPlaceSelectedCallback: PropTypes.func,
        paddingAdjustment: PropTypes.number,
        clearSubmitErrors: PropTypes.func,
        setValidState: PropTypes.func,
        submissionError: PropTypes.bool,
        types: PropTypes.array
    };

    constructor(props) {
        super(props);
        this.autocomplete = null;
        this.event = null;
        this.state = {
            addressSelected: false,
            active: false,
            error: false,
            touched: false,
            value: props.cachedAddress || '',
            lastValidAddressString: props.cachedAddress || ''
        };

        this.setUpGoogleAddressDropDownObserver();
    }

    setUpGoogleAddressDropDownObserver() {
        this.clickedAddressDropDown = false;
        this.bodyObserver = new MutationObserver(mutations => {
            this.addressFinderNode = mutations[0].addedNodes[0];

            if (
                this.addressFinderNode &&
                this.addressFinderNode.className.includes('pac-container')
            ) {
                this.addressFinderNode.addEventListener(
                    'mousedown',
                    this.clickedAddressCallBack
                );
                this.bodyObserver.disconnect();
            }
        });
        this.bodyObserver.observe(document.body, { childList: true, attributes: true });
    }

    clickedAddressCallBack = e => {
        if (e.path && e.path.some(el => el.className === 'pac-item'))
            this.clickedAddressDropDown = true;
    };

    componentDidMount() {
        this.setupAutocompleteInputField();
        if (this.props.cachedAddress) {
            this.autocomplete.getPlace();
            this.setState({
                error: false,
                active: false,
                addressSelected: true,
                fullAddress: this.props.previouslyCachedAddress,
                value: this.props.cachedAddress
            });

            this.handleCaching(this.props.previouslyCachedAddress);
            this.props.setValidState(true);
            this.clearFormLevelSubmissionErrors();
        }

        if (this.props.submissionError && this.state.error) {
            this.setState({ error: true });
        }
    }

    componentWillUnmount() {
        this.event.remove();

        this.addressFinderNode &&
            this.addressFinderNode.removeEventListener(
                'mousedown',
                this.clickedAddressCallBack
            );
    }

    clearFormLevelSubmissionErrors() {
        if (
            typeof this.props.clearSubmitErrors === 'function' &&
            this.props.clearSubmitErrors
        ) {
            this.props.clearSubmitErrors('address');
        }
    }

    handleCaching(addressObject) {
        if (this.props.formName) {
            this.props.formName.forEach(name => {
                for (let field in addressObject) {
                    this.props.change(name, field, addressObject[field]);
                }
            });
        }
        // cache values for later use
        this.props.cacheFormData({ ...addressObject });
    }

    setupAutocompleteInputField() {
        if (typeof google === 'undefined') return;
        const { types = ['(cities)'], componentRestrictions, bounds } = this.props;
        const config = { types, bounds };

        if (componentRestrictions) {
            config.componentRestrictions = componentRestrictions;
        }

        this.autocomplete = new google.maps.places.Autocomplete(this.refs.input, config); //eslint-disable-line
        this.autocompleteService = new google.maps.places.AutocompleteService(); //eslint-disable-line
        this.geocoder = new google.maps.Geocoder(); //eslint-disable-line

        if ((this.props.fields || []).length > 0) {
            this.autocomplete.setFields(this.props.fields);
        }
        this.event = this.autocomplete.addListener(
            'place_changed',
            this.onPlaceSelected.bind(this)
        );
    }

    onPlaceSelected(place) {
        if (!place) place = this.autocomplete.getPlace();
        if (!place || !place.address_components) return;

        const fullAddress = parseAddressComponents(place.address_components);

        if (!fullAddress || !fullAddress.postalCode) {
            this.setState({
                error: true,
                active: false,
                addressSelected: false,
                value: place.formatted_address
            });
            this.props.setValidState(false);
            this.props.addressValidationError(
                copyFor('validationErrors', 'googleAddress')
            );
            return;
        } else {
            this.setState({
                error: false,
                active: false,
                addressSelected: true,
                fullAddress,
                value: place.formatted_address
            });
            this.props.setValidState(true);
            this.setState({ lastValidAddressString: place.formatted_address });
        }

        if (this.props.onPlaceSelectedCallback) {
            this.props.onPlaceSelectedCallback(fullAddress);
        }

        this.handleCaching(fullAddress);
        this.clearFormLevelSubmissionErrors();

        this.handleBlur();
    }

    async fillInBasicallyCorrectAddress() {
        return new Promise(resolve => {
            if (!this.state.addressSelected && this.state.value) {
                this.autocompleteService.getPlacePredictions(
                    { input: this.state.value },
                    places => {
                        if (places && places.length === 1) {
                            this.geocoder.geocode(
                                { address: places[0].description },
                                structuredAddresses => {
                                    if (!structuredAddresses) resolve(false);
                                    else {
                                        this.onPlaceSelected(structuredAddresses[0]);
                                        resolve(true);
                                    }
                                }
                            );
                        } else resolve(false);
                    }
                );
            } else resolve(false);
        });
    }

    async handleBlur(e) {
        if (await this.fillInBasicallyCorrectAddress()) return;

        if (this.clickedAddressDropDown) {
            this.clickedAddressDropDown = false;
            return;
        }
        this.setState({ active: false });
        pollForValidAddressSelected(
            this.state.addressSelected,
            100,
            this.state.value !== '' ? 300 : 0
        ).then(() => {
            if (!this.state.addressSelected) {
                this.setState({ error: true });
                this.props.setValidState(false);
            } else {
                this.setState({ error: false });
                this.props.setValidState(true);
            }
        });
    }

    handleChange(e) {
        this.setState({ value: e.target.value });
        if (e.target.value === '') {
            this.setState({ addressSelected: false });
            this.props.setValidState(false);
        } else if (
            this.state.addressSelected &&
            this.state.lastValidAddressString &&
            e.target.value !== this.state.lastValidAddressString
        ) {
            this.setState({
                addressSelected: false
            });
            this.props.setValidState(false);
        }
    }

    handleFocus() {
        this.setState({ active: true, touched: true });
        if (this.props.onFocus) {
            this.props.onFocus();
        }
    }

    render() {
        const error = this.props.submissionError || this.state.error;
        return (
            <Container paddingAdjustment={this.props.paddingAdjustment || 0}>
                <Label
                    id="google_autocomplete__label"
                    className={error && !this.state.active && 'error'}
                    error={error}
                    value={this.state.value}
                    active={this.state.active}
                    paddingAdjustment={this.props.paddingAdjustment || 0}
                >
                    {!error || this.state.active
                        ? copyFor(
                              'formFields',
                              'fullAddress',
                              'general',
                              capitalization.eachWord
                          )
                        : copyFor(
                              'validationErrors',
                              'fullAddress',
                              'general',
                              capitalization.eachWord
                          )}
                </Label>
                <input
                    // STYLES SET IN GLOBAL - STYLED COMPONENTS BREAK AUTOCOMPLETE
                    ref="input" //eslint-disable-line
                    autoComplete="off"
                    className={`fs-block ${error &&
                        !this.state.active &&
                        'autocompleteError'}`}
                    id="google_autocomplete"
                    name="address"
                    onBlur={e => this.handleBlur(e)}
                    onChange={e => this.handleChange(e)}
                    onFocus={() => this.handleFocus()}
                    onKeyDown={e => {
                        if (e.keyCode === 13) {
                            if (this.autocomplete.getPlace() === undefined) {
                                e.preventDefault();
                            }
                            google.maps.event.trigger(this.autocomplete, 'place_changed');
                            return false;
                        }
                    }}
                    placeholder=""
                    type="text"
                    value={this.state.value}
                />
            </Container>
        );
    }
}

export default connect(
    state => {
        const previouslyCachedAddress = state.form.google_autocomplete
            ? state.form.google_autocomplete.values
            : state.cachedFormData;

        return {
            previouslyCachedAddress,
            cachedAddress: formatAddressToString(previouslyCachedAddress)
        };
    },
    {
        addressValidationError,
        cacheFormData,
        change
    }
)(ReactGoogleAutocomplete);
